import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionSummary, Box, Divider, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilterByOptions } from "../../../../../../../components/common/BriaTable/BriaTable";
import DebouncedInput from "../../../../../../../components/common/DebouncedInput/DebouncedInput";
import LoadingPlaceholder from "../../../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import { BRIA_PUBLIC_ORG_ID } from "../../../../../../../constants/OrgConstants";
import { useAppStore } from "../../../../../../../hooks/useStores";
import { Font } from "../../../../../../../models/font";
import { convertS3UriToHttpsUrl } from "../../../../../../../pages/Campaign/Brands/utils";
import { loadFont } from "../../../../../../../utils";
import useDesignEditorUtils, { FeatureType, Property } from "../../../../../../CustomUtils/UseDesignEditor";
import StepperNavigation from "../../../../../../StepperNavigation/StepperNavigation";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader";
import { DesignEditorContext } from "../../../../../../contexts/DesignEditor";
import { useActiveObject } from "../../../../../../hooks/useActiveObject";
import UseBrandConfigs from "../../../../../../hooks/useBrandConfigs";
import { useEditor } from "../../../../../../hooks/useEditor";
import { IStaticText, LayerType } from "../../../../../../types";
import { loadFonts } from "../../../../../../utils/fonts";
import styles from "./FontSelector.module.scss";
interface FontOption {
	key: string;
	value: number;
	src: string;
	orgId: string;
	label?: string;
}

interface FontGroup {
	name: string;
	options: FontOption[];
}

const FontSelector = () => {
	const { isPopupView } = useContext(DesignEditorContext);
	const { brandFontStore, campaignStore, brandsDefinitionStore, designEditorStore } = useAppStore();
	const editor = useEditor();
	const [fontsOptions, setFontsOptions] = useState<FontGroup[]>([]);
	const { EditAllAds } = useDesignEditorUtils();
	const activeObject = useActiveObject() as Required<IStaticText>;
	const validFonts = brandsDefinitionStore.globalSelectedBrand.brand_texts?.filter((bt, _i) => bt?.font?.src);
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.brands" });
	const [selectedFont, setSelectedFont] = useState(activeObject?.customFontName ?? activeObject?.fontFamily);
	const { modifyActiveObjectProperty } = UseBrandConfigs();

	useEffect(() => {
		brandFontStore.getAllOrgBrandFonts(true).then(() => {
			groupFonts();
		});
	}, []);

	useEffect(() => {
		fontsOptions && fontsOptions?.forEach((group: any) => group.options.forEach(loadFont));
	}, [fontsOptions]);
	useEffect(() => {
		const watcher = async () => {
			if (activeObject && activeObject?.type === "StaticText") {
				setSelectedFont(activeObject.customFontName ?? activeObject.fontFamily);
			}
		};
		if (editor) {
			editor.on("history:changed", watcher);
		}
		return () => {
			if (editor) {
				editor.off("history:changed", watcher);
			}
		};
	}, [editor, activeObject]);

	const handleFontFamilyChange = async (fnt: any, idx?: number) => {
		if (editor) {
			const font = {
				name: fnt.key ?? fnt.name,
				url: convertS3UriToHttpsUrl(fnt.src),
			};
			await loadFonts([font]);
			editor.objects.update(
				{
					fontFamily: font.name,
					fontURL: font.url,
					brandId: undefined,
					customFontName: idx ? getFontName(idx, font.name) : undefined,
				},
				undefined,
				!campaignStore.isEditingAllAds,
			);
			EditAllAds(LayerType.STATIC_TEXT, activeObject?.textType, FeatureType.FONT, font);
		}
	};
	const getFontName = (idx: number, fontName: string) => {
		return t("brandFont") + idx + " " + fontName;
	};

	const handleChange = (property: keyof typeof designEditorStore.activeObjectSettings, newValue: any) => {
		modifyActiveObjectProperty(activeObject, property, newValue);
	};

	const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const updatedFilterBy: FilterByOptions = {
			search: { text: e.target.value, fields: ["name", "updated_at"] },
		};
		brandFontStore.loadBrandFonts(true, updatedFilterBy).then(() => {
			groupFonts();
		});
	};

	const groupFonts = () => {
		const fontOptions = brandFontStore?.brandOrgFonts?.map((font: Font) => ({
			key: font.name,
			value: font.id,
			src: font.src,
			orgId: font.org_id,
		}));

		const groupedFontOptions = fontOptions.reduce(
			(groupedFontOptions: Record<string, FontOption[]>, font: FontOption) => {
				const orgId = font.orgId;
				if (!groupedFontOptions[orgId]) {
					groupedFontOptions[orgId] = [];
				}
				groupedFontOptions[orgId].push({
					key: font.key,
					label: font.key,
					value: font.value,
					src: font.src,
					orgId: font.orgId,
				});
				return groupedFontOptions;
			},
			{} as Record<string, FontOption[]>,
		);

		const privateFonts = Object.entries(groupedFontOptions)
			.filter(([orgId]) => orgId !== BRIA_PUBLIC_ORG_ID)
			.map(([, options]) => ({ name: "Private Fonts", options }));

		// Step 2: Get public fonts
		const publicFonts = Object.entries(groupedFontOptions)
			.filter(([orgId]) => orgId === BRIA_PUBLIC_ORG_ID)
			.map(([, options]) => ({ name: "Public Fonts", options }));

		// Combine private and public fonts
		const orgFonts = [...privateFonts, ...publicFonts];
		setFontsOptions(orgFonts);
	};

	return (
		<Box className={styles.fontSelectorContainer}>
			<MainTabHeader title="Fonts" />
			<Box className={`${styles.tabs} ${styles.fullHeight}`}>
				<Box className={styles.fontTabContent}>
					<Box className={styles.searchBoxContainer}>
						<DebouncedInput
							onChange={handleSearchChange}
							className={styles.customBriaInput}
							onKeyDown={(e) => e.stopPropagation()}
							height="40px"
							placeHolder={"Search"}
						/>
					</Box>
					<LoadingPlaceholder className={styles.loading} isLoading={brandFontStore.isLoading}>
						<Box className={styles.fontOptionsContainer}>
							{campaignStore.selectedTemplate?.is_branded_template &&
								validFonts &&
								validFonts?.length > 0 && (
									<>
										<Accordion
											defaultExpanded
											elevation={0}
											disableGutters
											TransitionProps={{ timeout: 200 }}
											className={styles.fontsOptionsAccordion}
										>
											<AccordionSummary
												className={styles.accordionSummary}
												expandIcon={<ExpandMore />}
											>
												<Typography className={styles.fontGroupTitle}>
													{t("brandFonts")}
												</Typography>
											</AccordionSummary>

											{brandsDefinitionStore.globalSelectedBrand.brand_texts?.map(
												(brandText, idx) =>
													brandText.font?.src ? (
														<Box
															key={idx}
															onClick={() => {
																if (brandText.font) {
																	handleChange(Property.FONT_TYPE, brandText.type);
																	handleFontFamilyChange(brandText.font, idx + 1);
																}
															}}
															className={clsx({
																[styles.fontItemContent]: true,
																[styles.selectedFont]:
																	brandText.type ===
																	designEditorStore.activeObjectSettings.fontType,
															})}
														>
															<Typography className={styles.itemLabel}>
																{t("font")} {idx + 1}
															</Typography>
															<Typography style={{ fontFamily: brandText.font?.name }}>
																{brandText.font?.name}
															</Typography>
														</Box>
													) : null,
											)}
										</Accordion>
										<Divider className={styles.divider} />
									</>
								)}

							{fontsOptions.map((group, groupIndex) => (
								<Accordion
									key={groupIndex}
									defaultExpanded
									elevation={0}
									disableGutters
									TransitionProps={{ timeout: 200 }}
									className={styles.fontsOptionsAccordion}
								>
									<AccordionSummary className={styles.accordionSummary} expandIcon={<ExpandMore />}>
										<Typography className={styles.fontGroupTitle}>{group.name}</Typography>
									</AccordionSummary>

									{group.options.map((font: any, index: number) => (
										<Box
											className={clsx({
												[styles.fontOption]: true,
												[styles.selectedFont]: selectedFont === font.label,
											})}
											style={{ fontFamily: font.label }}
											key={index}
											onClick={() => {
												handleChange(Property.FONT_TYPE, undefined);
												handleFontFamilyChange(font);
											}}
											id={font.value}
										>
											{font.key}
										</Box>
									))}

									{groupIndex !== fontsOptions.length - 1 && (
										<Divider key={`divider_${groupIndex}`} className={styles.divider} />
									)}
								</Accordion>
							))}
						</Box>
					</LoadingPlaceholder>
				</Box>
			</Box>
			{isPopupView && <StepperNavigation />}
		</Box>
	);
};

const ObservedComponent = observer(FontSelector);
export default ObservedComponent;
